"use client";

import { useCallback, useEffect, useState } from "react";
import {
  _registerConfirmHandler,
  type ConfirmDialogOptions,
} from "@/lib/notifications/confirmDialog";
import { ConfirmDialog } from "./ConfirmDialog";

/**
 * ConfirmDialogProvider — mount once w root layout, łączy global `confirmDialog()`
 * API z React state (renderowanie modal'a).
 *
 * **Usage:** dodaj `<ConfirmDialogProvider />` w `app/layout.tsx` raz.
 * Potem dowolny komponent może wywołać `await confirmDialog({...})` i czeka
 * na odpowiedź user'a.
 *
 * **Singleton handler:** Provider rejestruje swój handler w `_registerConfirmHandler`
 * w useEffect. Wywołanie `confirmDialog()` deleguje do tego handler'a, który
 * setState'uje `pendingDialog` -> komponent re-renderuje i pokazuje modal.
 *
 * **Single dialog at a time:** kolejne calls do `confirmDialog()` gdy poprzedni
 * jest open -> automatycznie zamykają poprzedni jako "cancel" i otwierają nowy.
 * (Edge case, raczej się nie zdarzy w normal UX.)
 */
export function ConfirmDialogProvider() {
  const [pending, setPending] = useState<{
    opts: ConfirmDialogOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  /* Rejestracja handler'a — sprzęga global API z lokalnym state. */
  useEffect(() => {
    const unregister = _registerConfirmHandler((opts) => {
      return new Promise<boolean>((resolve) => {
        setPending((prev) => {
          /* Jeżeli już jest otwarty dialog — auto-cancel poprzedniego */
          if (prev) prev.resolve(false);
          return { opts, resolve };
        });
      });
    });
    return unregister;
  }, []);

  const handleConfirm = useCallback(() => {
    setPending((prev) => {
      prev?.resolve(true);
      return null;
    });
  }, []);

  const handleCancel = useCallback(() => {
    setPending((prev) => {
      prev?.resolve(false);
      return null;
    });
  }, []);

  if (!pending) return null;

  return (
    <ConfirmDialog
      opts={pending.opts}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );
}
